import { Box, type SxProps, type Theme, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { CSSProperties, FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { DashboardTypography } from "../../../components/Dashboard/DashboardTypography";
import { InsightsIcon } from "../../../components/Dashboard/InsightsIcon";
import { InHousePieChart } from "../../../components/UIToolkit/Graphs/PieChart/InHousePieChart";
import type { InHousePieDataItem } from "../../../components/UIToolkit/Graphs/PieChart/types";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { ALPHA, LABOR_MIX } from "../../../theme/tokens";
import type { ManagementCenterAveragesAndTotals } from "../hooks";
import { useStaffAppAccountStats } from "../useStaffAppAccountStats";
import { costToKDollars } from "../utils";

/** Responsive KPI stat-card height — shared with the multi-facility KPI row so both modes' stat rows match. */
export const STATISTICS_CARD_HEIGHT = {
	xs: "100px",
	xl: "110px",
	xxl: "120px",
} as const satisfies SxProps<Theme>;
const commonSx = {
	flex: 1,
	minWidth: 0,
	height: STATISTICS_CARD_HEIGHT,
} as const satisfies SxProps<Theme>;

type Props = {
	gap: CSSProperties["gap"];
	data?: ManagementCenterAveragesAndTotals;
	isActual: boolean;
	laborCostEnabled: boolean;
	hideWage?: boolean;
	departmentId?: string;
};
export const Statistics: FC<Props> = ({ gap, data, isActual, laborCostEnabled, hideWage, departmentId: departmentIdOverride }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;

	// Ordered baseline → premium so the slices climb the LABOR_MIX ramp the way the Cost and Staff
	// Mix stacks do; the legend renders in this order too.
	const pieData: InHousePieDataItem[] | undefined = data
		? [
				{ name: "FULL TIME", value: data.fulltimeHours, color: LABOR_MIX.BASE },
				{ name: "OVERTIME", value: data.overtimeHours, color: LABOR_MIX.OVERTIME_ALL },
				{ name: "AGENCY", value: data.agencyHours, color: LABOR_MIX.AGENCY },
			]
		: undefined;
	const staffAppStats = useStaffAppAccountStats(departmentId);

	const handleTotalCostClick = () => {
		track("cost_deep_dive_opened", { department_id: departmentId });
		navigate(`/${departmentId}/cost-deep-dive?${searchParams.toString()}`);
	};

	return (
		<Box
			sx={{
				display: "flex",
				gap: 1,
				width: "100%",
				justifyContent: "start",
			}}
		>
			{!hideWage && (
				<CommonDashboardBox
					sx={{
						display: "flex",
						alignItems: "start",
						gap: gap,
						flexShrink: 0,
						alignSelf: "start",
						height: STATISTICS_CARD_HEIGHT,
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "start",
							gap: gap,
						}}
					>
						<CommonDashboardHeader leading={<InsightsIcon />} label="Average PPD" />
						<Box
							sx={{
								width: {
									xs: "130px",
									xl: "220px",
								},
							}}
						>
							<DashboardTypography
								mainText={`$${data ? (isActual ? data.avgActualPPD : data.avgScheduledPPD).toFixed(2) : "-"}`}
								subText={data?.targetPPD ? `/ $${data?.targetPPD.toFixed(2) ?? "-"}` : undefined}
								mainTextTooltip={`${isActual ? "Actual" : "Scheduled"} PPD`}
								subTextTooltip={"Target PPD"}
							/>
						</Box>
					</Box>
					<Tooltip title={t("% of hours")} placement="top">
						<Box component="span" sx={{ display: "inline-flex" }}>
							<InHousePieChart
								height={"80px"}
								innerRadius={"40%"}
								outerRadius={"60%"}
								// No pre-highlighted slice: `renderActiveShape` draws a leader line, dot and percent label
								// for the active index, and on a card this short that label lands on the "Average PPD"
								// title. The legend beside the donut already gives every percentage, so the resting state
								// loses nothing -- hover still shows the full active shape.
								data={pieData}
								dontRenderInnerLabel
								legendSxOverride={{ marginLeft: { xs: 0, xl: 4 } }}
							/>
						</Box>
					</Tooltip>
				</CommonDashboardBox>
			)}
			<CommonDashboardBox sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: gap, ...commonSx }}>
				<CommonDashboardHeader leading={<InsightsIcon />} label="Average HPPD" />
				<DashboardTypography
					mainText={`${data ? (isActual ? data.avgActualHPPD : data.avgScheduledHPPD).toFixed(2) : "-"}`}
					subText={data?.targetHPPD ? `/ ${data?.targetHPPD.toFixed(2) ?? "-"}` : undefined}
					mainTextTooltip={`${isActual ? "Actual" : "Scheduled"} HPPD`}
					subTextTooltip={"Target HPPD"}
				/>
			</CommonDashboardBox>
			{!hideWage && (
				<CommonDashboardBox
					onClick={handleTotalCostClick}
					sx={(theme) => ({
						display: "flex",
						flexDirection: "column",
						alignItems: "start",
						gap: gap,
						...commonSx,
						cursor: "pointer",
						"&:hover": { boxShadow: `0px 2px 12px 0px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
						transition: "box-shadow 0.2s",
					})}
				>
					<CommonDashboardHeader leading={<InsightsIcon />} label="Total Cost" />
					<DashboardTypography
						verticalGap={gap}
						mainText={`${costToKDollars(data?.totalCost) ?? "$-"}`}
						mainTextTooltip={`${isActual ? "Actual" : "Scheduled"} Cost`}
						smallText={`OT ${costToKDollars(data?.totalOvertimeCost) ?? "$-"} | Agency ${costToKDollars(data?.totalAgencyCost) ?? "$-"}${laborCostEnabled ? ` | PTO ${costToKDollars(data?.totalPtoCost) ?? "$-"}` : ""}`}
					/>
				</CommonDashboardBox>
			)}
			<CommonDashboardBox sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: gap, ...commonSx }}>
				<CommonDashboardHeader leading={<InsightsIcon />} label="Occupancy" />
				<DashboardTypography
					verticalGap={gap}
					mainText={`${data?.occupancyRate?.toFixed(1) ?? "-"}%`}
					smallText={`${data?.totalAverageCensus.toFixed(0) ?? "-"} / ${data?.totalBeds ?? "-"} patients`}
				/>
			</CommonDashboardBox>
			<CommonDashboardBox
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					gap: gap,
					flexShrink: 0,
					alignSelf: "start",
					height: STATISTICS_CARD_HEIGHT,
				}}
			>
				<CommonDashboardHeader leading={<InsightsIcon />} label="Staff App Accounts" />
				<DashboardTypography
					verticalGap={gap}
					mainText={`${staffAppStats?.monthlyActive ?? "-"}`}
					subText="Monthly Active"
					smallText={`${staffAppStats?.activeInPastOrNextMonth ?? "-"} Active | ${staffAppStats?.onBoarded ?? "-"} On Boarded`}
					mainTextTooltip={"Monthly active users on the staff app in the past 30 days"}
					subTextTooltip={"Monthly active users on the staff app in the past 30 days"}
					smallTextTooltip={
						"Number of nurses who have worked or are scheduled to work in the past or next month who are active on the staff app | Number of nurses who have been onboarded to use the staff app"
					}
				/>
			</CommonDashboardBox>
		</Box>
	);
};
